import {
  ADMIN_AVATAR_FALLBACK_BG,
  ADMIN_AVATAR_FALLBACK_TEXT,
  ADMIN_CAMPAIGN_STATUS_PILL,
} from '@/config/adminChartTheme';
import React, { useEffect, useState } from 'react';
import ChartCard from '../ChartCard';
import { fetchTopCampaignsByGmv } from '../../../services/campaignAnalyticsService';
import millify from 'millify';

interface TopCampaign {
  campaignId: string;
  campaignName: string;
  brandName: string;
  brandImageUrl: string;
  status: string;
  gmv: number;
  cashbackPaid: number;
  roas: number;
  submissionsCount: number;
  creatorCount: number;
}

interface TopCampaignsTableProps {
  refreshToken?: number;
}

const TopCampaignsTable: React.FC<TopCampaignsTableProps> = ({
  refreshToken,
}) => {
  const [data, setData] = useState<TopCampaign[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTopCampaignsByGmv(5)
      .then((d) => setData(Array.isArray(d) ? d : []))
      .finally(() => setLoading(false));
  }, [refreshToken]);

  return (
    <ChartCard
      title="Top Campaigns"
      subtitle="By GMV generated"
      loading={loading}
    >
      {data.length > 0 ? (
        <div className="space-y-3">
          {data.map((c, i) => (
            <div
              key={c.campaignId}
              className="flex items-center gap-3 p-3 rounded-2xl hover:bg-[#FAFAFA] transition-colors"
            >
              <span className="text-xs font-bold text-[#9B919D] w-5 shrink-0">
                {i + 1}
              </span>
              <div
                className={`w-9 h-9 rounded-xl overflow-hidden shrink-0 flex items-center justify-center ${ADMIN_AVATAR_FALLBACK_BG}`}
              >
                {c.brandImageUrl ? (
                  <img
                    src={c.brandImageUrl}
                    alt={c.brandName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span
                    className={`text-xs font-bold ${ADMIN_AVATAR_FALLBACK_TEXT}`}
                  >
                    {c.brandName?.[0]}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#172B4D] truncate">
                  {c.campaignName ?? c.brandName}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-[#9B919D] truncate">
                    {c.brandName}
                  </span>
                  <span className="text-[#E5E9EB]">·</span>
                  <span
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${ADMIN_CAMPAIGN_STATUS_PILL[c.status] ?? ADMIN_CAMPAIGN_STATUS_PILL.draft}`}
                  >
                    {c.status}
                  </span>
                  <span className="text-[10px] text-[#9B919D]">
                    {c.creatorCount} creators
                  </span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-bold text-[#172B4D]">
                  €{millify(c.gmv)}
                </p>
                <p className="text-[10px] text-[#9B919D]">{c.roas}x ROAS</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="h-48 flex items-center justify-center">
          <p className="text-[#9B919D] text-sm">No campaign data</p>
        </div>
      )}
    </ChartCard>
  );
};

export default TopCampaignsTable;
